import type { CompetitorRecord } from "@/lib/compare/types";

const PRICING = "https://www.revid.ai/pricing";
const AI_INSTRUCTIONS = "https://www.revid.ai/ai-instructions";
const CHECKED = "2026-10-08";

export const REVID: CompetitorRecord = {
  slug: "revid",
  name: "Revid",
  aliases: ["Revid.ai", "revid ai", "Typeframes"],
  url: "https://www.revid.ai",
  pricingUrl: PRICING,
  category: "faceless",
  positioning:
    "Where short-form creators grow: viral-video intelligence, script-to-video with premium AI video models, Auto-Mode workers and one-click publishing.",
  plans: [
    {
      name: "Free",
      monthly: 0,
      metered: "Free signup credits, amount not stated",
      includes: [
        "No credit card",
        "Viral-video search",
        "Deep analysis up to 60 per day",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Hobby",
      monthly: 39,
      metered: "Credits per month not shown on the plan card",
      includes: [
        "Text or link to video",
        "Remix",
        "Editable shorts",
        "For occasional, hands-on creation",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Pricing page card only. No annual price, credit count or worker count is shown, and the plan is absent from the ai-instructions page. Same monthly price as Growth.",
    },
    {
      name: "Growth",
      monthly: 39,
      annualPerMonth: 32,
      metered: "2,000 credits per month; about 40 to 80 credits per typical 30-second video",
      includes: [
        "3 Auto-Mode workers",
        "5 watched channels",
        "Seedance 2.5 up to 4K",
        "100+ tools, avatars, face swap",
        "AI voices in 70+ languages",
        "REST API, MCP server, CLI",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "$99 shown struck through with the words Discount locked in forever. Annual total not published.",
    },
    {
      name: "Elite",
      monthly: 89,
      annualPerMonth: 74,
      metered: "5,000 credits per month",
      includes: ["5 Auto-Mode workers", "20 watched channels", "Voice cloning"],
      sourceUrl: AI_INSTRUCTIONS,
      checkedAt: CHECKED,
      note: "Listed on the ai-instructions page (last updated Oct 2, 2026) only. Not on the live pricing page cards.",
    },
    {
      name: "Ultra",
      monthly: 199,
      annualPerMonth: 166,
      metered: "12,000 credits per month, slider to 200,000",
      includes: ["10 Auto-Mode workers", "50 watched channels", "Voice cloning"],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Slider tiers above 12,000 credits: 30,000 for $459, 50,000 for $749, 100,000 for $1,459, 200,000 for $2,799 a month.",
    },
  ],
  entryPlanName: "Growth",
  entryPlanIncludes:
    "2,000 credits, 3 Auto-Mode workers, 5 watched channels, API access; Hobby costs the same $39 a month with fewer stated features",
  freeTier: "Free signup credits with no credit card; the amount is not published.",
  trial: "No timed trial; new accounts get free credits instead.",
  meteringUnit:
    "Credits per generation; a typical 30-second video with Pro moving images and a voiceover costs about 40 to 80 credits",
  exportResolution: "720p, 1080p or 4K at 30 or 60 fps",
  features: {
    longToShort: { v: "partial", note: "Audio to Video tool, no ranked clipper" },
    momentRanking: { v: "no", note: "Viral library ranks other creators' posts" },
    reframing: { v: "n/a", note: "Not described on their pages" },
    maxSource: { v: "n/a", note: "No source cap published" },
    layouts: { v: "n/a", note: "Not enumerated" },
    redditStory: { v: "partial", note: "Reddit posts to video, no gameplay template" },
    fakeText: { v: "no", note: "Their Crayo page lists it as Crayo's" },
    aiImages: { v: "yes", note: "Veo 3, Sora 2, Seedance 2.5, Hailuo" },
    voiceover: { v: "yes", note: "70+ languages via ElevenLabs" },
    voiceCloning: { v: "partial", note: "Elite and Ultra only" },
    avatars: { v: "yes", note: "Avatars, face swap, AI influencer" },
    timelineEditor: { v: "partial", note: "Scene-based editor, not multi-track" },
    captionStyles: { v: "text", note: "16 styles" },
    captionLanguages: { v: "text", note: "100+" },
    bRoll: { v: "partial", note: "Stock and AI visuals, no gameplay library" },
    fourK: { v: "yes", note: "Up to 4K at 60 fps" },
    scheduling: { v: "yes", note: "TikTok, Reels, Shorts, plus Auto-Mode" },
    analytics: { v: "partial", note: "Post stats and watched channels" },
    socialTracker: { v: "partial", note: "Watch 5 to 50 channels by plan" },
    api: { v: "yes", note: "REST v3, MCP server, CLI on paid plans" },
    webhooks: { v: "yes" },
    web: { v: "yes" },
    desktop: { v: "no", note: "Not advertised" },
    mobile: { v: "no", note: "Not advertised" },
    teamSeats: { v: "n/a", note: "Not stated on the pricing page" },
    freeTier: { v: "yes", note: "Signup credits, amount unpublished" },
    trial: { v: "no", note: "Free credits instead of a timed trial" },
    metering: { v: "text", note: "40 to 80 credits per 30-second video" },
    rollover: { v: "no", note: "Plan credits expire; packs do not" },
    refunds: { v: "text", note: "Not stated on the pricing page" },
    cancelInApp: { v: "text", note: "Cancel anytime; mechanism not described" },
  },
  billingTerms: {
    refunds: "No refund terms appear on the pricing page as of Oct 8, 2026.",
    cancel:
      "The pricing page says you can cancel anytime and keep access to the end of the billing period. It does not describe where the cancel control is.",
    sourceUrl: PRICING,
    checkedAt: CHECKED,
  },
  quotes: [
    {
      text: "The output quality is so low that you burn through credits",
      date: "2026-08-30",
      url: "https://www.trustpilot.com/review/revid.ai",
      evidences: "metering",
    },
  ],
  theirCrayoPages: [
    {
      url: "https://www.revid.ai/vs/crayo",
      claims: [
        "Page marked Facts checked September 2026",
        "~$13/month (billed yearly); Hobby is $160/year with 250 workflow credits per month",
        "Month-to-month rates are higher and not publicly listed",
        "Crayo has no free plan or trial, and its refund policy says all sales are final",
        "Users export and post manually",
        "No series automation or scheduler",
        "A template-based web editor",
        "No public API or MCP server",
        "Template sameness; saturated templates reduce reach",
        "Concedes a fast idea-to-clip flow, 15+ viral caption styles, praised support and a low entry price",
      ],
    },
  ],
  verification: {
    method: "fetch",
    date: CHECKED,
    note: `Pricing page ${PRICING} fetched directly on Oct 8, 2026 for Free, Hobby, Growth and Ultra, the credit packs and the 40 to 80 credits per video line. Elite ($89/mo, $74/mo annual, 5,000 credits) appears only on ${AI_INSTRUCTIONS}, last updated Oct 2, 2026, and is not on the live pricing page cards; a browser check should confirm whether Elite is purchasable. Hobby and Growth both render at $39/mo and Hobby shows no annual price or credit count; a browser check of the pricing toggle should confirm both before publishing. Annual totals are not published anywhere and are omitted.`,
  },
  seoPattern: "revid.ai/vs/<competitor> (25 pages plus a /vs hub) and an LLM-targeted /ai-instructions page; publishes /vs/crayo",
};
